import React, { useMemo, useState } from "react";
import {
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
  Users as UsersIcon,
  Pencil,
  Trash2,
  UserCog,
} from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 8;

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const timeOf = (value) => {
  const t = new Date(value).getTime();
  return isNaN(t) ? 0 : t;
};

const AdminUsersTable = ({
  users = [],
  loading = false,
  currentUserId,
  onEdit,
  onDelete,
}) => {
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);

  // Role options taken from the data.
  const roleOptions = useMemo(() => {
    const set = new Set();
    users.forEach((u) => u.role && set.add(u.role));
    return [...set];
  }, [users]);

  // Filter by search query + role.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      const name = (u.name || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const userRole = (u.role || "").toLowerCase();
      const matchesQuery =
        !q || name.includes(q) || email.includes(q) || userRole.includes(q);
      const matchesRole = role === "all" || u.role === role;
      return matchesQuery && matchesRole;
    });
  }, [users, query, role]);

  // Sort the filtered list.
  const sorted = useMemo(() => {
    const list = [...filtered];
    switch (sortBy) {
      case "oldest":
        return list.sort((a, b) => timeOf(a.created_at) - timeOf(b.created_at));
      case "name-asc":
        return list.sort((a, b) =>
          (a.name || "").localeCompare(b.name || "")
        );
      case "name-desc":
        return list.sort((a, b) =>
          (b.name || "").localeCompare(a.name || "")
        );
      case "newest":
      default:
        return list.sort((a, b) => timeOf(b.created_at) - timeOf(a.created_at));
    }
  }, [filtered, sortBy]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = sorted.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleSearch = (e) => {
    setQuery(e.target.value);
    setPage(1);
  };

  const handleRole = (value) => {
    setRole(value);
    setPage(1);
  };

  const handleSort = (value) => {
    setSortBy(value);
    setPage(1);
  };

  const thClass =
    "text-gray-500 font-semibold uppercase text-[11px] tracking-wider";

  return (
    <div className="rounded-md border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-200">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={query}
            onChange={handleSearch}
            placeholder="Search by name, email or role…"
            className="w-full rounded-md border border-gray-200 bg-gray-50 pl-9 pr-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#b32126] focus:bg-white focus:ring-2 focus:ring-[#b32126]/15 focus:outline-none transition"
          />
        </div>

        <Select value={role} onValueChange={handleRole}>
          <SelectTrigger className="w-[160px] h-auto rounded-md border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:border-[#b32126] focus:bg-white focus:ring-2 focus:ring-[#b32126]/15">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {roleOptions.map((r) => (
              <SelectItem key={r} value={r}>
                {r.charAt(0).toUpperCase() + r.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={handleSort}>
          <SelectTrigger className="w-[160px] h-auto rounded-md border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:border-[#b32126] focus:bg-white focus:ring-2 focus:ring-[#b32126]/15">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
            <SelectItem value="name-asc">Name A–Z</SelectItem>
            <SelectItem value="name-desc">Name Z–A</SelectItem>
          </SelectContent>
        </Select>

        <span className="ml-auto shrink-0 text-sm text-gray-500">
          {sorted.length} {sorted.length === 1 ? "user" : "users"}
        </span>
      </div>

      {/* Table */}
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50/80 hover:bg-gray-50/80 border-b border-gray-200">
            <TableHead className={`${thClass} pl-6`}>Name</TableHead>
            <TableHead className={thClass}>Email</TableHead>
            <TableHead className={thClass}>Role</TableHead>
            <TableHead className={thClass}>Joined</TableHead>
            <TableHead className={`${thClass} text-right pr-6`}>
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-gray-500 py-12">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading users…
                </span>
              </TableCell>
            </TableRow>
          ) : pageItems.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="py-12">
                <div className="flex flex-col items-center gap-2 text-gray-400">
                  <UsersIcon className="h-8 w-8" />
                  <span className="text-sm">
                    {query || role !== "all"
                      ? "No users match your filters."
                      : "No users found."}
                  </span>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            pageItems.map((user) => {
              const isSelf = user.id === currentUserId;

              return (
                <TableRow
                  key={user.id}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50/60"
                >
                  {/* Name + avatar initial */}
                  <TableCell className="pl-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 shrink-0 rounded-full bg-[#b32126]/10 text-[#b32126] flex items-center justify-center font-semibold">
                        {(user.name || "?").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        {isSelf && (
                          <span className="text-xs text-gray-400">You</span>
                        )}
                      </div>
                    </div>
                  </TableCell>

                  {/* Email */}
                  <TableCell className="text-gray-600 py-4">
                    {user.email || <span className="text-gray-400">—</span>}
                  </TableCell>

                  {/* Role */}
                  <TableCell className="py-4">
                    <span className="inline-flex items-center gap-1 text-gray-700">
                      <UserCog className="h-4 w-4 text-gray-400" />
                      {user.role || "user"}
                    </span>
                  </TableCell>

                  {/* Joined date */}
                  <TableCell className="text-gray-600 py-4 whitespace-nowrap">
                    {formatDate(user.created_at)}
                  </TableCell>

                  {/* Actions */}
                  <TableCell className="py-4 pr-6">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => onEdit?.(user)}
                        title="Edit user"
                        className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-sm text-gray-600 hover:border-[#b32126] hover:text-[#b32126] transition-colors"
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </button>

                      <button
                        type="button"
                        disabled={isSelf}
                        onClick={() => onDelete?.(user)}
                        title={
                          isSelf
                            ? "You cannot delete your own account"
                            : "Delete user"
                        }
                        className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-gray-200 text-red-600 hover:border-red-300 hover:bg-red-50 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:border-gray-200 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {!loading && sorted.length > 0 && (
        <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-200">
          <span className="text-sm text-gray-500">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setPage(currentPage - 1)}
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`h-8 w-8 rounded-md text-sm font-medium transition-colors ${
                  p === currentPage
                    ? "bg-[#b32126] text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => setPage(currentPage + 1)}
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersTable;
